import { Box, Button, Card, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFormContext } from 'react-hook-form';
import { useI18n } from 'iomart-hooks';
import { client } from 'iomart-hooks';
import { CardBlock } from 'src/shorts/blocks/CardBlock';
import PrivateFilesBlock from 'src/components/private-files/PrivateFilesBlock';
import {
  RHFProduct,
  RHFProductBrand,
  RHFProductCategory,
  RHFTextField,
  RHFUpload,
} from 'src/components/hook-form';
import RHFMoney from 'src/components/hook-form/RHFMoney';
import RHFTags from 'src/components/hook-form/RHFTags';
import { RHFArrayBlock } from 'src/shorts/forms/RHFArrayBlock';
import RHFNumber from 'src/components/hook-form/RHFNumber';
import RHFFloat from 'src/components/hook-form/RHFFloat';
import type { ProductFormData } from './productFormUtils';

type ProductFormContentProps = {
  /** Показывать блок «Товары» (комплекты) */
  showProductsBlock?: boolean;
  /** Показывать блок Private files (метки) */
  showPrivateFilesBlock?: boolean;
  /** Блоки друг за другом (одна колонка), иначе две колонки (изображения слева, форма справа) */
  stackLayout?: boolean;
};

export function ProductFormContent({
  showProductsBlock = true,
  showPrivateFilesBlock = true,
  stackLayout = false,
}: ProductFormContentProps) {
  const { t } = useI18n(false);
  const { setValue, getValues } = useFormContext<ProductFormData>();
  const defaultValues = useFormContext<ProductFormData>().watch();
  const productId = Number((defaultValues as any)?.productId ?? 0);
  const disabled = (defaultValues as any)?.status !== 'drafts';
  const products = (defaultValues as any)?.products || [];

  const leftSize = stackLayout ? { xs: 12 } : { xs: 12, md: 4 };
  const rightSize = stackLayout ? { xs: 12 } : { xs: 12, md: 8 };

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid size={leftSize}>
        <Card>
          <Box sx={{ p: 3 }}>
            <RHFUpload multiple thumbnail name="images" />
          </Box>
        </Card>
      </Grid>
      <Grid size={rightSize}>
        <Stack spacing={3}>
          <CardBlock title={t('Main Information')}>
            <Box rowGap={3} display="grid">
              <RHFTextField name="name" label="Name" />
              <RHFProductCategory name="category" label="Category" />
              <RHFProductBrand name="brand" label="Brand" />
              <RHFTextField name="shortDescription" label="Short Description" />
              <Stack direction="row" gap={2}>
                <RHFMoney name="rrc" label="Retail price" />
                <RHFMoney name="oldPrice" label="Old price" />
                <RHFTextField name="hsCode" label="HS Code" size="small" />
              </Stack>
              <RHFTags name="barcodes" label="Barcodes" freeSolo options={[]} multiple />
              <Stack direction="row" alignItems="flex-end" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    try {
                      const res = await client.query({ generateProductArticle: true });
                      const article = res.generateProductArticle;
                      const current: string[] = getValues('articles') || [];
                      const next = Array.from(new Set([...(current || []), article]));
                      setValue('articles', next, { shouldValidate: true, shouldDirty: true });
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                >
                  {t('Generate article')}
                </Button>
              </Stack>
              <RHFTags name="articles" label="Articles" freeSolo options={[]} multiple />
              <Stack direction="row" gap={2}>
                <RHFNumber name="length" label="Length" symbol="cm." />
                <RHFNumber name="width" label="Width" symbol="cm." />
                <RHFNumber name="height" label="Height" symbol="cm." />
                <RHFFloat name="weight" label="Weight" symbol="kg." />
              </Stack>
            </Box>
          </CardBlock>

          {showProductsBlock && (!disabled || products.length > 0) && (
            <CardBlock title={t('Products')}>
              <RHFArrayBlock
                appendData={{ count: 1 }}
                addTitle="Add new product"
                name="products"
                disabled={disabled}
              >
                {({ index }) => (
                  <>
                    <RHFProduct
                      name={`products[${index}].product`}
                      label="Product"
                      disabled={disabled}
                      isSet={false}
                    />
                    <RHFTextField
                      size="small"
                      name={`products[${index}].count`}
                      label="Count"
                      InputLabelProps={{ shrink: true }}
                      disabled={disabled}
                    />
                  </>
                )}
              </RHFArrayBlock>
            </CardBlock>
          )}

          <CardBlock title={t('Description (HTML)')}>
            <Box rowGap={3} display="grid">
              <RHFTextField multiline name="description" />
            </Box>
          </CardBlock>

          {showPrivateFilesBlock && (
            <PrivateFilesBlock
              entityType="product"
              entityId={productId}
              editable={!!productId}
              title="Label files (private)"
              presetAssetKeys={['barcode']}
              allowedMimeTypes={['application/pdf', 'application/octet-stream']}
              allowedExtensions={['.nlbl', '.pdf']}
            />
          )}
        </Stack>
      </Grid>
    </Grid>
  );
}
